<template>
  <teleport to="body">
    <div
      ref="root"
      :class="['coaching-call', minimized ? 'coaching-call--minimized' : '']"
      role="dialog"
      :aria-modal="!minimized"
      :aria-label="title"
      @pointermove="poke"
      @pointerdown="poke"
      @focusin="poke"
    >
      <!-- T00AYNISXGY6 — check picture, sound and devices before the other side sees you. -->
      <div
        v-if="inLobby"
        class="coaching-call-lobby"
      >
        <v-card
          class="coaching-call-lobby-card"
          max-width="720"
          width="100%"
        >
          <v-card-text>
            <div class="d-flex align-center mb-4">
              <img
                v-if="logo && !logoFailed"
                :src="logo"
                :alt="t('common.a11y.logo')"
                class="coaching-call-logo mr-4"
                @error="logoFailed = true"
              >
              <div>
                <h2 class="text-h6">
                  {{ t('individualCoaching.call.lobbyHeadline') }}
                </h2>
                <div class="text-medium-emphasis">
                  {{ occurrence.title }} · {{ peerName }}
                </div>
              </div>
            </div>
            <v-alert
              v-if="call.error.value"
              type="error"
              variant="tonal"
              class="mb-4 coaching-call-error"
            >
              {{ t(`individualCoaching.call.${call.error.value}`) }}
            </v-alert>
            <v-row>
              <v-col
                cols="12"
                md="7"
              >
                <div
                  :class="['coaching-call-tile', 'coaching-call-tile--preview', localSpeaking ? 'coaching-call-tile--speaking' : '']"
                >
                  <video
                    v-show="hasLocalVideo"
                    ref="previewVideo"
                    class="coaching-call-video coaching-call-video--mirrored"
                    autoplay
                    playsinline
                    muted
                    :aria-label="t('individualCoaching.call.localVideo')"
                  />
                  <!-- While camera and microphone start: a light spinner with readable text, no initials yet. -->
                  <div
                    v-if="call.status.value === 'idle'"
                    class="coaching-call-preview-loading"
                    role="status"
                  >
                    <v-progress-circular
                      indeterminate
                      color="white"
                      size="36"
                    />
                    <span>{{ t('individualCoaching.call.previewLoading') }}</span>
                  </div>
                  <div
                    v-else-if="!hasLocalVideo"
                    class="coaching-call-avatar"
                    aria-hidden="true"
                  >
                    {{ initials(ownName) }}
                  </div>
                </div>
                <p
                  v-if="call.status.value === 'preview' && call.cameras.value.length === 0"
                  class="text-caption text-medium-emphasis mt-2 mb-0"
                >
                  {{ t('individualCoaching.call.noCamera') }}
                </p>
              </v-col>
              <v-col
                cols="12"
                md="5"
              >
                <v-select
                  v-if="call.cameras.value.length > 0"
                  :model-value="call.cameraId.value"
                  :items="deviceItems(call.cameras.value)"
                  :menu-props="DEVICE_MENU"
                  :label="t('individualCoaching.call.camera')"
                  density="compact"
                  class="coaching-call-camera"
                  @update:model-value="(id: string) => call.selectDevice('video', id)"
                />
                <v-select
                  v-if="call.microphones.value.length > 0"
                  :model-value="call.microphoneId.value"
                  :items="deviceItems(call.microphones.value)"
                  :menu-props="DEVICE_MENU"
                  :label="t('individualCoaching.call.microphone')"
                  density="compact"
                  class="coaching-call-microphone"
                  @update:model-value="(id: string) => call.selectDevice('audio', id)"
                />
                <div class="text-caption mb-1">
                  {{ t('individualCoaching.call.micLevel') }}
                </div>
                <v-progress-linear
                  :model-value="Math.min(100, localLevel * 400)"
                  color="primary"
                  height="8"
                  rounded
                  :aria-label="t('individualCoaching.call.micLevel')"
                  class="mb-4 coaching-call-meter"
                />
                <div class="d-flex ga-2">
                  <v-btn
                    :icon="call.muted.value ? Icons.MICROPHONE_OFF : Icons.MICROPHONE"
                    :color="call.muted.value ? 'error' : undefined"
                    variant="tonal"
                    :aria-label="call.muted.value ? t('individualCoaching.call.unmute') : t('individualCoaching.call.mute')"
                    :title="call.muted.value ? t('individualCoaching.call.unmute') : t('individualCoaching.call.mute')"
                    :aria-pressed="call.muted.value"
                    @click="call.toggleMute()"
                  />
                  <v-btn
                    v-if="call.cameras.value.length > 0"
                    :icon="call.videoOff.value ? Icons.VIDEO_OFF : Icons.VIDEO"
                    :color="call.videoOff.value ? 'error' : undefined"
                    variant="tonal"
                    :aria-label="call.videoOff.value ? t('individualCoaching.call.videoOn') : t('individualCoaching.call.videoOff')"
                    :aria-pressed="call.videoOff.value"
                    @click="call.toggleVideo()"
                  />
                </div>
                <p class="text-caption text-medium-emphasis mt-4 mb-0">
                  {{ t('individualCoaching.call.lobbyHint') }}
                </p>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
              variant="text"
              @click="close"
            >
              {{ t('common.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              class="coaching-call-join"
              :prepend-icon="Icons.VIDEO"
              :disabled="call.error.value === 'unsupported' || call.status.value === 'joining'"
              :loading="call.status.value === 'joining'"
              @click="call.start()"
            >
              {{ t('individualCoaching.call.join') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>

      <!-- Dark like Meet or Zoom, and independent of the tenant's colours. -->
      <v-theme-provider
        v-else
        theme="dark"
        class="coaching-call-stage-theme"
      >
        <div
          ref="stage"
          :class="['coaching-call-stage', sharingLayout ? 'coaching-call-stage--sharing' : '']"
        >
          <!-- T00O4VGPVISQ — a shared screen fills the stage; both cameras stack small in a corner. -->
          <div
            v-if="sharingLayout"
            class="coaching-call-tile coaching-call-tile--screen"
          >
            <!-- A shared screen has no caption track to offer. -->
            <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
            <video
              v-if="call.remoteScreenStream.value"
              ref="screenVideo"
              class="coaching-call-video"
              autoplay
              playsinline
              muted
              :aria-label="t('individualCoaching.call.screenOf', {name: displayPeerName})"
            />
            <div
              v-else
              class="coaching-call-overlay-text coaching-call-sharing-self"
              role="status"
            >
              <v-icon
                :icon="Icons.MONITOR_SHARE"
                size="large"
                class="mb-2"
              />
              <div>{{ t('individualCoaching.call.youAreSharing') }}</div>
            </div>
          </div>
          <!-- T00O4VGPVISQ — while a screen is shared both cameras form one small stack that
               can be dragged into any corner; otherwise this wrapper does not exist for the layout. -->
          <div
            :class="['coaching-call-stack', sharingLayout ? `coaching-call-tile--${selfCorner}` : '', sharingLayout && dragging ? 'coaching-call-tile--dragging' : '']"
            :style="sharingLayout ? dragStyle : undefined"
            :title="sharingLayout ? t('individualCoaching.call.moveSelf') : undefined"
            @pointerdown="(event: PointerEvent) => sharingLayout && startDrag(event)"
          >
            <!-- The other side. Its video element stays mounted: it also plays the sound. -->
            <div :class="['coaching-call-tile', 'coaching-call-tile--remote', remoteSpeaking ? 'coaching-call-tile--speaking' : '']">
              <!-- A live call has no caption track to offer. -->
              <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
              <video
                v-show="hasRemoteVideo"
                ref="remoteVideo"
                class="coaching-call-video"
                autoplay
                playsinline
                :aria-label="t('individualCoaching.call.remoteVideo', {name: displayPeerName})"
              />
              <div
                v-if="!hasRemoteVideo"
                class="coaching-call-avatar coaching-call-avatar--large"
                aria-hidden="true"
              >
                <img
                  v-if="!call.remoteStream.value && logo && !logoFailed"
                  :src="logo"
                  alt=""
                  class="coaching-call-logo"
                  @error="logoFailed = true"
                >
                <template v-else>
                  {{ initials(displayPeerName) }}
                </template>
              </div>
              <div
                v-if="!call.remoteStream.value || call.status.value !== 'connected'"
                class="coaching-call-overlay-text coaching-call-status"
                role="status"
                aria-live="polite"
              >
                {{ statusText }}
              </div>
              <div class="coaching-call-name">
                <v-icon
                  v-if="call.remoteState.value.muted"
                  :icon="Icons.MICROPHONE_OFF"
                  size="small"
                  :aria-label="t('individualCoaching.call.micOff')"
                  class="mr-1"
                />
                {{ displayPeerName }}
              </div>
            </div>

            <!-- T00NV8JRQZCY — own picture: mirrored like a mirror, drag it into any corner. -->
            <div
              v-show="!minimized"
              :class="['coaching-call-tile', 'coaching-call-tile--self', sharingLayout ? '' : `coaching-call-tile--${selfCorner}`, localSpeaking ? 'coaching-call-tile--speaking' : '', dragging && !sharingLayout ? 'coaching-call-tile--dragging' : '']"
              :style="sharingLayout ? undefined : dragStyle"
              :title="sharingLayout ? undefined : t('individualCoaching.call.moveSelf')"
              @pointerdown="(event: PointerEvent) => !sharingLayout && startDrag(event)"
            >
              <video
                v-show="hasLocalVideo"
                ref="localVideo"
                class="coaching-call-video coaching-call-video--mirrored"
                autoplay
                playsinline
                muted
                :aria-label="t('individualCoaching.call.localVideo')"
              />
              <div
                v-if="!hasLocalVideo"
                class="coaching-call-avatar"
                aria-hidden="true"
              >
                {{ initials(ownName) }}
              </div>
              <div class="coaching-call-name">
                <v-icon
                  v-if="call.muted.value"
                  :icon="Icons.MICROPHONE_OFF"
                  size="x-small"
                  :aria-label="t('individualCoaching.call.micOff')"
                  class="mr-1"
                />
                {{ t('individualCoaching.call.you') }}
              </div>
            </div>
          </div>

          <!-- Top: title, recording, duration, quality, minimize. -->
          <div :class="['coaching-call-bar', 'coaching-call-bar--top', controlsVisible ? '' : 'coaching-call-bar--hidden']">
            <span
              v-if="!minimized"
              class="coaching-call-title"
            >{{ occurrence.title }}</span>
            <v-chip
              v-if="recording.state.value === 'recording' || recording.state.value === 'stopping'"
              color="error"
              size="small"
              variant="flat"
              :prepend-icon="Icons.RECORD"
              class="coaching-call-recording"
            >
              {{ recording.state.value === 'stopping' ? t('individualCoaching.call.recording.stopping') : t('individualCoaching.call.recording.running') }}
            </v-chip>
            <v-chip
              v-else-if="recording.state.value === 'requesting'"
              size="small"
              variant="tonal"
            >
              {{ t('individualCoaching.call.recording.waiting') }}
            </v-chip>
            <v-spacer />
            <span
              v-if="call.status.value === 'connected' || call.elapsed.value > 0"
              class="coaching-call-duration"
              :aria-label="t('individualCoaching.call.duration')"
            >{{ duration }}</span>
            <span
              v-if="call.quality.value && !minimized"
              class="coaching-call-quality ml-3"
              :title="t(`individualCoaching.call.quality.${call.quality.value}`)"
            >
              <v-icon
                :icon="qualityIcon"
                size="small"
                :aria-label="t(`individualCoaching.call.quality.${call.quality.value}`)"
              />
            </span>
            <v-btn
              v-if="canPictureInPicture && hasRemoteVideo && !minimized"
              :icon="Icons.PICTURE_IN_PICTURE_BOTTOM_RIGHT"
              variant="text"
              size="small"
              class="ml-2 coaching-call-pip coaching-call-icon"
              :aria-label="t('individualCoaching.call.pip')"
              :title="t('individualCoaching.call.pip')"
              @click="togglePictureInPicture"
            />
            <v-btn
              v-if="away"
              :icon="Icons.ARROW_LEFT"
              variant="text"
              size="small"
              class="ml-1 coaching-call-back coaching-call-icon"
              :aria-label="t('individualCoaching.call.backToRoom')"
              :title="t('individualCoaching.call.backToRoom')"
              @click="emit('back')"
            />
            <v-btn
              :icon="minimized ? Icons.ARROW_EXPAND : Icons.WINDOW_MINIMIZE"
              variant="text"
              size="small"
              class="ml-1 coaching-call-minimize coaching-call-icon"
              :aria-label="minimized ? t('individualCoaching.call.maximize') : t('individualCoaching.call.minimize')"
              :title="minimized ? t('individualCoaching.call.maximize') : t('individualCoaching.call.minimize')"
              @click="minimized = !minimized"
            />
          </div>

          <!-- T00NV8JRQZCY — controls inside the picture, hidden while nobody moves the mouse. -->
          <div :class="['coaching-call-bar', 'coaching-call-bar--bottom', controlsVisible ? '' : 'coaching-call-bar--hidden']">
            <v-btn
              :icon="call.muted.value ? Icons.MICROPHONE_OFF : Icons.MICROPHONE"
              :color="call.muted.value ? 'error' : undefined"
              variant="flat"
              :size="minimized ? 'small' : 'default'"
              :aria-label="call.muted.value ? t('individualCoaching.call.unmute') : t('individualCoaching.call.mute')"
              :title="call.muted.value ? t('individualCoaching.call.unmute') : t('individualCoaching.call.mute')"
              :aria-pressed="call.muted.value"
              class="coaching-call-mute coaching-call-control"
              @click="call.toggleMute()"
            />
            <template v-if="!minimized">
              <v-btn
                :icon="call.videoOff.value ? Icons.VIDEO_OFF : Icons.VIDEO"
                :color="call.videoOff.value ? 'error' : undefined"
                variant="flat"
                :aria-label="call.videoOff.value ? t('individualCoaching.call.videoOn') : t('individualCoaching.call.videoOff')"
                :title="call.videoOff.value ? t('individualCoaching.call.videoOn') : t('individualCoaching.call.videoOff')"
                :aria-pressed="call.videoOff.value"
                class="coaching-call-video-toggle coaching-call-control"
                @click="call.toggleVideo()"
              />
              <!-- Clicks on the selects inside must not close the menu (Vuetify closes on content click by default). -->
              <v-menu
                location="top"
                :close-on-content-click="false"
              >
                <template #activator="{props: menu}">
                  <v-btn
                    v-bind="menu"
                    :icon="Icons.TUNE_VARIANT"
                    variant="flat"
                    :aria-label="t('individualCoaching.call.devices')"
                    :title="t('individualCoaching.call.devices')"
                    class="coaching-call-devices coaching-call-control"
                  />
                </template>
                <v-card
                  min-width="300"
                  class="pa-3"
                >
                  <v-select
                    v-if="call.cameras.value.length > 0"
                    :model-value="call.cameraId.value"
                    :items="deviceItems(call.cameras.value)"
                    :menu-props="DEVICE_MENU"
                    :label="t('individualCoaching.call.camera')"
                    density="compact"
                    @update:model-value="(id: string) => call.selectDevice('video', id)"
                  />
                  <v-select
                    :model-value="call.microphoneId.value"
                    :items="deviceItems(call.microphones.value)"
                    :menu-props="DEVICE_MENU"
                    :label="t('individualCoaching.call.microphone')"
                    density="compact"
                    hide-details
                    @update:model-value="(id: string) => call.selectDevice('audio', id)"
                  />
                </v-card>
              </v-menu>
              <v-btn
                v-if="call.canShareScreen"
                :icon="call.sharing.value ? Icons.MONITOR_OFF : Icons.MONITOR_SHARE"
                variant="flat"
                :aria-label="call.sharing.value ? t('individualCoaching.call.stopShare') : t('individualCoaching.call.shareScreen')"
                :title="call.sharing.value ? t('individualCoaching.call.stopShare') : t('individualCoaching.call.shareScreen')"
                :aria-pressed="call.sharing.value"
                class="coaching-call-share coaching-call-control"
                @click="call.toggleScreenShare()"
              />
              <!-- T00EXMB3XI6N — only the coach starts a recording; both can end it. -->
              <v-btn
                v-if="isCoach && recording.supported && recording.state.value === 'off' && call.status.value === 'connected'"
                :icon="Icons.RECORD_REC"
                variant="flat"
                :aria-label="t('individualCoaching.call.recording.start')"
                :title="t('individualCoaching.call.recording.start')"
                class="coaching-call-record coaching-call-control"
                @click="recording.request()"
              />
              <v-btn
                v-if="recording.state.value === 'recording' || recording.state.value === 'requesting'"
                :icon="Icons.STOP"
                color="error"
                variant="flat"
                :aria-label="t('individualCoaching.call.recording.stop')"
                :title="t('individualCoaching.call.recording.stop')"
                class="coaching-call-record-stop coaching-call-control"
                @click="recording.stop()"
              />
              <!-- T00MM9OMVDXS — chat in the call, stored as the thread of this session. -->
              <v-badge
                :content="chatUnread"
                :model-value="chatUnread > 0 && !chatOpen"
                color="error"
              >
                <v-btn
                  :icon="Icons.MESSAGE_TEXT_OUTLINE"
                  :color="chatOpen ? 'primary' : undefined"
                  variant="flat"
                  :aria-label="t('individualCoaching.call.chat.open')"
                  :title="t('individualCoaching.call.chat.open')"
                  :aria-pressed="chatOpen"
                  class="coaching-call-chat-toggle coaching-call-control"
                  @click="chatOpen = !chatOpen"
                />
              </v-badge>
            </template>
            <v-btn
              color="error"
              variant="flat"
              :icon="Icons.PHONE_HANGUP"
              :size="minimized ? 'small' : 'default'"
              :aria-label="t('individualCoaching.call.hangup')"
              :title="t('individualCoaching.call.hangup')"
              class="coaching-call-hangup coaching-call-control"
              @click="hangup"
            />
          </div>

          <coaching-call-chat
            v-if="!minimized"
            :client-id="clientId"
            :session-id="occurrence.sessionId"
            :open="chatOpen"
            :send-message="call.sendMessage"
            :on-message="call.onMessage"
            @close="chatOpen = false"
            @unread="(count: number) => chatUnread = count"
          />

          <div
            v-if="call.status.value === 'ended'"
            class="coaching-call-ended"
          >
            <p class="text-h6 mb-4">
              {{ t('individualCoaching.call.ended') }}
            </p>
            <v-btn
              color="primary"
              variant="flat"
              @click="close"
            >
              {{ t('common.close') }}
            </v-btn>
          </div>
        </div>
      </v-theme-provider>

      <!-- T00EXMB3XI6N — the client decides; nothing is recorded before "Ich stimme zu". -->
      <v-dialog
        :model-value="recording.state.value === 'consent'"
        max-width="520"
        persistent
        :aria-label="t('individualCoaching.call.recording.consentTitle')"
      >
        <v-card class="coaching-call-consent">
          <v-card-title>{{ t('individualCoaching.call.recording.consentTitle') }}</v-card-title>
          <v-card-text>
            <p>{{ t('individualCoaching.call.recording.consentText', {name: displayPeerName}) }}</p>
            <p>{{ t('individualCoaching.call.recording.consentWhere') }}</p>
            <p class="mb-0">
              {{ t('individualCoaching.call.recording.consentWithdraw') }}
            </p>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
              variant="text"
              class="coaching-call-consent-no"
              @click="recording.decline()"
            >
              {{ t('individualCoaching.call.recording.consentNo') }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              class="coaching-call-consent-yes"
              @click="recording.accept()"
            >
              {{ t('individualCoaching.call.recording.consentYes') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </teleport>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {Icons, useEventBus} from '@simpleforyou/client-core';
import {Events} from '@/common/utils/Events';
import {useStore} from '@/common/store/store';
import {logoUrl} from '@/composables/layout/baseLayout';
import {SPEAKING_THRESHOLD, useAudioLevel} from '@/composables/individualCoaching/useAudioLevel';
import {useCallRecording} from '@/composables/individualCoaching/useCallRecording';
import {type CallDevice, useCoachingCall} from '@/composables/individualCoaching/useCoachingCall';
import type {ISessionOccurrence} from '@/types/components/individualCoaching/IIndividualCoaching';
//================= [VUE COMPONENTS] ==========================
import CoachingCallChat from '@/components/individualCoaching/call/CoachingCallChat.vue';

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const CONTROLS_HIDE_MS = 3000;

const props = defineProps<{
  clientId: string;
  occurrence: ISessionOccurrence;
  /** Name of the other side, shown while waiting. */
  peerName: string;
  /** Whether the viewer is the coach (starts recordings). */
  isCoach?: boolean;
  /** T009OSKBRBOH — the viewer is on another page; the call shrinks and offers the way back. */
  away?: boolean;
  /** T00IILDDEQF2 — back after a reload: join right away, the click on "resume" was the gesture. */
  autoJoin?: boolean;
}>();
const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'back'): void;
  // T00IILDDEQF2 — hung up: nothing to resume after a reload any more.
  (event: 'hungup'): void;
}>();

const {t} = useI18n();

/** The device lists get their own class so their text stays light on the dark menu. */
const DEVICE_MENU = {contentClass: 'coaching-call-device-menu'};
const store = useStore();
const eventBus = useEventBus();
const call = useCoachingCall(props.clientId, props.occurrence.sessionId, props.occurrence.occurrenceDate);
const recording = useCallRecording(call, props.clientId, props.occurrence.sessionId, props.occurrence.occurrenceDate, () => props.isCoach === true);
const {level: localLevel} = useAudioLevel(call.localStream);
const {level: remoteLevel} = useAudioLevel(call.remoteStream);

const root = ref<HTMLElement>();
const stage = ref<HTMLElement>();
const previewVideo = ref<HTMLVideoElement>();
const localVideo = ref<HTMLVideoElement>();
const remoteVideo = ref<HTMLVideoElement>();
const screenVideo = ref<HTMLVideoElement>();
const minimized = ref(false);
const controlsVisible = ref(true);
const corner = ref<Corner>('bottom-right');
const dragging = ref(false);
const dragOffset = ref<{x: number; y: number} | null>(null);
const logo = logoUrl();
const logoFailed = ref(false);
const chatOpen = ref(false);
const chatUnread = ref(0);
const canPictureInPicture = typeof document !== 'undefined' && document.pictureInPictureEnabled === true;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

const title = computed(() => `${t('individualCoaching.call.headline')} · ${props.occurrence.title}`);
const inLobby = computed(() => ['idle', 'preview', 'joining'].includes(call.status.value) || (call.status.value === 'error' && !call.join.value));
const displayPeerName = computed(() => call.peerName.value ?? props.peerName);
const ownName = computed(() => `${store.userData?.contact?.firstname ?? ''} ${store.userData?.contact?.lastname ?? ''}`.trim());
// T00O4VGPVISQ — the camera stays its own picture while a screen is shared.
const hasLocalVideo = computed(() => !!call.localStream.value?.getVideoTracks().length && !call.videoOff.value);
const hasRemoteVideo = computed(() => !!call.remoteStream.value?.getVideoTracks().length && !call.remoteState.value.videoOff);
/** Someone shares a screen: it fills the stage, the two cameras stack small in a movable corner box. */
const sharingLayout = computed(() => !minimized.value && (call.sharing.value || !!call.remoteScreenStream.value));
const localSpeaking = computed(() => !call.muted.value && localLevel.value > SPEAKING_THRESHOLD);
const remoteSpeaking = computed(() => !call.remoteState.value.muted && remoteLevel.value > SPEAKING_THRESHOLD);
const qualityIcon = computed(() => ({good: Icons.SIGNAL_CELLULAR_3, medium: Icons.SIGNAL_CELLULAR_2, poor: Icons.SIGNAL_CELLULAR_1}[call.quality.value ?? 'good']));
const duration = computed(() => {
  const seconds = call.elapsed.value;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
});
// An open chat covers the right side; the own picture moves out of its way.
const selfCorner = computed<Corner>(() => (chatOpen.value ? corner.value.replace('right', 'left') as Corner : corner.value));
const dragStyle = computed(() => (dragOffset.value ? {left: `${dragOffset.value.x}px`, top: `${dragOffset.value.y}px`, right: 'auto', bottom: 'auto'} : {}));
const statusText = computed(() => {
  const name = displayPeerName.value;
  switch (call.status.value) {
    case 'joining':
    case 'connecting':
      return t('individualCoaching.call.connecting');
    case 'waiting':
      return name ? t('individualCoaching.call.waiting', {name}) : t('individualCoaching.call.waitingAnonymous');
    case 'connected':
      return t('individualCoaching.call.connected', {name});
    case 'reconnecting':
      return t('individualCoaching.call.reconnecting');
    case 'ended':
      return t('individualCoaching.call.ended');
    default:
      return '';
  }
});

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

function deviceItems(devices: CallDevice[]) {
  return devices.map((device) => ({title: device.label, value: device.deviceId}));
}

// Controls stay while connecting or waiting; once connected they fade after a pause.
function poke(): void {
  controlsVisible.value = true;
  if (hideTimer) {
    clearTimeout(hideTimer);
  }
  hideTimer = setTimeout(() => {
    if (call.status.value === 'connected' && !minimized.value) {
      controlsVisible.value = false;
    }
  }, CONTROLS_HIDE_MS);
}

function startDrag(event: PointerEvent): void {
  const tile = event.currentTarget as HTMLElement;
  const bounds = stage.value?.getBoundingClientRect();
  if (!bounds) {
    return;
  }
  const tileBounds = tile.getBoundingClientRect();
  const grabX = event.clientX - tileBounds.left;
  const grabY = event.clientY - tileBounds.top;
  dragging.value = true;
  const move = (moveEvent: PointerEvent) => {
    dragOffset.value = {x: moveEvent.clientX - bounds.left - grabX, y: moveEvent.clientY - bounds.top - grabY};
  };
  const up = (upEvent: PointerEvent) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    dragging.value = false;
    dragOffset.value = null;
    // Snap to the nearest corner.
    const right = upEvent.clientX > bounds.left + bounds.width / 2;
    const bottom = upEvent.clientY > bounds.top + bounds.height / 2;
    corner.value = `${bottom ? 'bottom' : 'top'}-${right ? 'right' : 'left'}` as Corner;
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

async function togglePictureInPicture(): Promise<void> {
  if (document.pictureInPictureElement) {
    await document.exitPictureInPicture().catch(() => undefined);
  } else {
    await remoteVideo.value?.requestPictureInPicture().catch(() => undefined);
  }
}

async function hangup(): Promise<void> {
  emit('hungup');
  await recording.stop();
  if (document.pictureInPictureElement) {
    await document.exitPictureInPicture().catch(() => undefined);
  }
  await call.hangup();
}

async function close(): Promise<void> {
  await hangup();
  emit('close');
}

// Video elements change between lobby and stage; always hand them the current streams.
function attachStreams(): void {
  for (const element of [previewVideo.value, localVideo.value]) {
    if (element && element.srcObject !== call.localStream.value) {
      element.srcObject = call.localStream.value;
    }
  }
  if (screenVideo.value && screenVideo.value.srcObject !== call.remoteScreenStream.value) {
    screenVideo.value.srcObject = call.remoteScreenStream.value;
  }
  if (remoteVideo.value && remoteVideo.value.srcObject !== call.remoteStream.value) {
    remoteVideo.value.srcObject = call.remoteStream.value;
  }
}
watch([call.localStream, call.remoteStream, call.remoteScreenStream, inLobby, sharingLayout], () => {
  void nextTick(attachStreams);
}, {flush: 'post'});

// T009OSKBRBOH — on another page a running call steps aside instead of covering
// it. Before joining there is nothing to keep: leaving the preview closes it.
watch(() => props.away, (away) => {
  if (!away) {
    return;
  }
  if (inLobby.value) {
    void close();
  } else {
    minimized.value = true;
  }
});

watch(() => recording.declined.value, (declined) => {
  if (declined) {
    eventBus.emit(Events.NOTIFY, t('individualCoaching.call.recording.declined', {name: displayPeerName.value}));
  }
});
watch(() => recording.failed.value, (failed) => {
  if (failed) {
    eventBus.emit(Events.NOTIFY, t('individualCoaching.call.recording.failed'));
  }
});

onMounted(async () => {
  poke();
  await call.preview();
  attachStreams();
  if (props.autoJoin && call.status.value === 'preview') {
    await call.start();
  }
});
onBeforeUnmount(() => {
  if (hideTimer) {
    clearTimeout(hideTimer);
  }
});

defineExpose({call, recording, minimized, corner, controlsVisible, duration, chatOpen, chatUnread, initials, hangup, close, poke});
</script>

<style>
/*
 * The device lists open in an overlay outside the call, where the global
 * list rule (dark --font-color !important, in user.scss with the app id)
 * would paint dark text on a dark menu, while a tenant's styles can turn the
 * menu white. Background and text are therefore both fixed: dark like the
 * call, light text. Both selectors are needed to outweigh the global rule
 * in the coach and the coachee layout.
 */
#user-application .coaching-call-device-menu .v-list,
.coaching-call-device-menu .v-list {
  background: #2a2b2e !important;
}

#user-application .coaching-call-device-menu .v-list > .v-list-item,
.coaching-call-device-menu .v-list > .v-list-item {
  color: rgba(255, 255, 255, 0.92) !important;
}
</style>

<style scoped>
.coaching-call {
  position: fixed;
  inset: 0;
  /* Above the app bar and navigation (~1000), below Vuetify overlays (2000+): dialogs and menus of the call stay on top. */
  z-index: 1500;
  background: #0f1115;
  color: #fff;
  display: flex;
}

.coaching-call--minimized {
  inset: auto 16px 16px auto;
  width: 320px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
}

.coaching-call-lobby {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(var(--primary-break, 15, 17, 21), 0.12);
  overflow-y: auto;
}

.coaching-call-lobby-card {
  color: rgb(var(--v-theme-on-surface));
}

.coaching-call-logo {
  max-height: 48px;
  max-width: 140px;
  object-fit: contain;
}

.coaching-call-stage {
  position: relative;
  flex: 1;
  overflow: hidden;
}

.coaching-call-tile {
  position: relative;
  background: #1b1e24;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.15s ease;
}

.coaching-call-tile--speaking {
  box-shadow: 0 0 0 3px rgb(var(--v-theme-primary));
}

.coaching-call-tile--preview {
  aspect-ratio: 16 / 9;
  width: 100%;
}

.coaching-call-tile--remote {
  position: absolute;
  inset: 0;
  border-radius: 0;
}

.coaching-call-tile--self {
  position: absolute;
  width: min(24%, 240px);
  aspect-ratio: 16 / 9;
  cursor: grab;
  touch-action: none;
  z-index: 2;
}

.coaching-call-tile--dragging {
  cursor: grabbing;
  transition: none;
}

.coaching-call-tile--top-left { top: 72px; left: 16px; }
.coaching-call-tile--top-right { top: 72px; right: 16px; }
.coaching-call-tile--bottom-left { bottom: 96px; left: 16px; }
.coaching-call-tile--bottom-right { bottom: 96px; right: 16px; }

.coaching-call-stack {
  display: contents;
}

/* The shared screen fills the stage; both cameras stack small in a corner and move together. */
.coaching-call-stage--sharing .coaching-call-tile--screen {
  position: absolute;
  inset: 0;
  border-radius: 0;
}

.coaching-call-stage--sharing .coaching-call-stack {
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(24%, 240px);
  cursor: grab;
  touch-action: none;
  z-index: 2;
}

.coaching-call-stage--sharing .coaching-call-tile--remote,
.coaching-call-stage--sharing .coaching-call-tile--self {
  position: relative;
  inset: auto;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  cursor: inherit;
}

/* Own picture on top, the other side right below it. */
.coaching-call-stage--sharing .coaching-call-tile--self {
  order: -1;
}

.coaching-call-stage--sharing .coaching-call-avatar--large {
  width: 56px;
  height: 56px;
  font-size: 1.25rem;
}

.coaching-call-sharing-self {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
}

.coaching-call-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.coaching-call-tile--self .coaching-call-video,
.coaching-call-tile--preview .coaching-call-video {
  object-fit: cover;
}

.coaching-call-video--mirrored {
  transform: scaleX(-1);
}

.coaching-call-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
}

.coaching-call-avatar--large {
  width: 140px;
  height: 140px;
  font-size: 3rem;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.coaching-call--minimized .coaching-call-avatar--large {
  width: 72px;
  height: 72px;
  font-size: 1.5rem;
}

.coaching-call-name {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.55);
  font-size: 0.8rem;
  display: flex;
  align-items: center;
}

.coaching-call-preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #fff;
  font-size: 0.95rem;
  text-align: center;
  padding: 0 16px;
}

.coaching-call-overlay-text {
  position: absolute;
  bottom: 30%;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 1.1rem;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

.coaching-call-bar {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  z-index: 3;
  transition: opacity 0.25s ease;
}

.coaching-call-bar--top {
  top: 0;
  background: linear-gradient(rgba(0, 0, 0, 0.55), transparent);
}

.coaching-call-bar--bottom {
  bottom: 0;
  justify-content: center;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
  padding-bottom: 20px;
}

.coaching-call--minimized .coaching-call-bar {
  padding: 6px 8px;
}

.coaching-call-bar--hidden {
  opacity: 0;
  pointer-events: none;
}

.coaching-call-stage-theme {
  display: flex;
  flex: 1;
}

/* Glass buttons with white icons; set explicitly so tenant CSS cannot turn them white on white. */
.coaching-call-control:not(.bg-error):not(.bg-primary) {
  background-color: rgba(255, 255, 255, 0.16) !important;
  color: #fff !important;
}

.coaching-call-control:not(.bg-error):not(.bg-primary):hover {
  background-color: rgba(255, 255, 255, 0.26) !important;
}

.coaching-call-control.bg-error {
  /* A firm red: the dark theme's error colour is a pale pink. */
  background-color: #e53935 !important;
  color: #fff !important;
}

.coaching-call-control.bg-primary {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
}

.coaching-call-icon {
  color: #fff !important;
}

.coaching-call-title {
  font-weight: 600;
}

.coaching-call-duration {
  font-variant-numeric: tabular-nums;
}

.coaching-call-ended {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 17, 21, 0.85);
  z-index: 4;
}
</style>
